import { sound } from './sound';

const TAP_SLOP_PX = 10;

export function fitText(node: HTMLElement, params?: { minSize?: number; step?: number; mode?: 'width' | 'height' | 'both'; padding?: number }) {
	let minSize = params?.minSize ?? 8;
	let step = params?.step ?? 0.5;
	let mode = params?.mode ?? 'width';
	let padding = params?.padding ?? 0;

	function overflows(): boolean {
		if (mode === 'width') return node.scrollWidth > node.clientWidth - padding;
		if (mode === 'height') return node.scrollHeight > node.clientHeight - padding;
		return node.scrollWidth > node.clientWidth - padding || node.scrollHeight > node.clientHeight - padding;
	}

	function fit() {
		if (!node.isConnected) return;

		// Reset any previously applied inline size so we measure the natural CSS size
		node.style.fontSize = '';

		// Skip hidden elements
		if (node.clientWidth === 0 && node.clientHeight === 0) return;

		// If it already fits, nothing to do
		if (!overflows()) return;

		// Step down until text fits or we hit the floor
		let size = parseFloat(getComputedStyle(node).fontSize);
		while (overflows() && size > minSize) {
			size -= step;
			node.style.fontSize = `${size}px`;
		}
	}

	// Run after first paint so layout is settled
	requestAnimationFrame(fit);

	// Re-run whenever the element or its parent changes size (theme switch, orientation, etc.)
	const ro = new ResizeObserver(fit);
	ro.observe(node);
	if (node.parentElement) ro.observe(node.parentElement);

	return {
		update(newParams?: { minSize?: number; step?: number; mode?: 'width' | 'height' | 'both'; padding?: number }) {
			minSize = newParams?.minSize ?? 8;
			step = newParams?.step ?? 0.5;
			mode = newParams?.mode ?? 'width';
			padding = newParams?.padding ?? 0;
			requestAnimationFrame(fit);
		},
		destroy() {
			ro.disconnect();
		}
	};
}

// Moves the node to document.body so position:fixed/absolute content escapes
// any ancestor overflow:hidden / transform clipping. iOS Safari clips fixed
// descendants by an overflow:hidden ancestor, which traps dropdowns inside the nav.
export function portal(node: HTMLElement, target: HTMLElement | string = 'body') {
	const dest = typeof target === 'string' ? document.querySelector(target) : target;
	if (dest) dest.appendChild(node);
	return {
		destroy() {
			node.parentNode?.removeChild(node);
		}
	};
}

export function btn(node: HTMLElement, disabled = false) {
	let isDisabled = disabled;
	let startX = 0;
	let startY = 0;
	let cancelled = false;
	let pressing = false;

	function onenter() {
		if (isDisabled) return;
		sound.hover();
	}

	function onleave() {
		if (isDisabled) return;
	}

	function pressDown() {
		node.style.transition = 'transform 40ms ease-out, filter 40ms ease-out';
		node.style.transform = 'translateY(2px) scale(0.97)';
		node.style.filter = 'brightness(0.9)';
	}

	function pressUp() {
		node.style.transition = 'transform 140ms cubic-bezier(0.34, 1.56, 0.64, 1), filter 140ms ease-out';
		node.style.transform = '';
		node.style.filter = '';
	}

	function ondown(e: PointerEvent) {
		if (isDisabled) return;
		startX = e.clientX;
		startY = e.clientY;
		cancelled = false;
		pressing = true;
		pressDown();
	}

	function onmove(e: PointerEvent) {
		if (!pressing || cancelled) return;
		const dx = e.clientX - startX;
		const dy = e.clientY - startY;
		if (dx * dx + dy * dy > TAP_SLOP_PX * TAP_SLOP_PX) {
			cancelled = true;
			pressUp();
		}
	}

	function oncancel() {
		cancelled = true;
		pressing = false;
		pressUp();
	}

	function onup() {
		if (isDisabled) return;
		pressUp();
		if (pressing && !cancelled) sound.tap();
		pressing = false;
	}

	node.addEventListener('pointerenter', onenter);
	node.addEventListener('pointerleave', onleave);
	node.addEventListener('pointerdown', ondown);
	node.addEventListener('pointermove', onmove);
	node.addEventListener('pointerup', onup);
	node.addEventListener('pointercancel', oncancel);

	return {
		update(newDisabled: boolean) {
			isDisabled = newDisabled;
			if (isDisabled) node.style.transform = '';
		},
		destroy() {
			node.removeEventListener('pointerenter', onenter);
			node.removeEventListener('pointerleave', onleave);
			node.removeEventListener('pointerdown', ondown);
			node.removeEventListener('pointermove', onmove);
			node.removeEventListener('pointerup', onup);
			node.removeEventListener('pointercancel', oncancel);
		}
	};
}
